/* ─── Interactive components: Button, TextField, Checkbox, Picker, Slider, ActionRow ─── */

import { useCallback, useEffect, useRef, useState } from 'react';

import { useArtifactSendEvent, useArtifactDispatch } from '../store';
import { formatTextual } from '../textual';
import { useValueClock, type VectorClock } from '../useValueClock';

// ─── Button ─────────────────────────────────────────────────────────────

interface ArtifactButtonProps {
  text?: unknown;
  data?: unknown;
  primary?: boolean;
  busy?: boolean;
  disabled?: boolean;
  __node_id?: string;
}

export const ArtifactButton = (props: ArtifactButtonProps) => {
  const { text, data = null, primary = false, busy = false, disabled = false, __node_id } = props;
  const sendEvent = useArtifactSendEvent();
  const isDisabled = busy || disabled;

  return (
    <button
      type="button"
      className={`a-button ${primary ? 'a-button-primary' : 'a-button-outline'}`}
      disabled={isDisabled}
      aria-busy={busy || undefined}
      onClick={() => {
        if (__node_id) sendEvent(__node_id, 'click', { kind: 'ButtonClicked', type: 'click', data: data ?? text });
      }}
    >
      {formatTextual(text)}
    </button>
  );
};

// ─── TextField ──────────────────────────────────────────────────────────

export function ArtifactTextField({
  value: valueProp = '',
  label = '',
  variant = 'shortText',
  checks,
  __node_id,
  __clock,
}: {
  value?: string;
  label?: unknown;
  variant?: string;
  checks?: Array<{ pattern?: string; message?: string }>;
  __node_id?: string;
  __clock?: VectorClock;
}) {
  const sendEvent = useArtifactSendEvent();
  const dispatch = useArtifactDispatch();
  const [localValue, setLocalValue] = useState(valueProp);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const clock = useValueClock(__clock);

  // Sync from backend when prop changes — gated on the vector clock so a
  // server snapshot computed before our latest edit doesn't clobber it.
  useEffect(() => {
    if (!clock.isFresh()) return;
    clock.accept();
    setLocalValue(valueProp);
    if (__node_id) dispatch({ type: 'SET_LIVE_VALUE', cuid: Number(__node_id), name: 'value', value: valueProp });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [__clock, valueProp, __node_id, dispatch]);

  const validate = useCallback(
    (v: string) => {
      if (!checks) return null;
      for (const check of checks) {
        if (check.pattern && !new RegExp(check.pattern).test(v)) {
          return check.message ?? 'Invalid';
        }
      }
      return null;
    },
    [checks],
  );

  const handleChange = (v: string) => {
    setLocalValue(v);
    setError(validate(v));
    if (__node_id) dispatch({ type: 'SET_LIVE_VALUE', cuid: Number(__node_id), name: 'value', value: v });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (__node_id) sendEvent(__node_id, 'change', { kind: 'TextFieldChanged', value: v, _clock: clock.bump() });
    }, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && variant !== 'longText') {
      e.preventDefault();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (__node_id) sendEvent(__node_id, 'submit', { kind: 'TextFieldSubmit', value: localValue, _clock: clock.bump() });
    }
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const inputType =
    variant === 'number'
      ? 'number'
      : variant === 'date'
        ? 'date'
        : variant === 'obscured'
          ? 'password'
          : 'text';

  if (variant === 'longText') {
    return (
      <div className="a-textfield">
        {label != null && <label className="a-textfield-label">{formatTextual(label)}</label>}
        <textarea
          className="a-textfield-input a-textfield-textarea"
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          rows={4}
        />
        {error && <span className="a-textfield-error">{error}</span>}
      </div>
    );
  }

  return (
    <div className="a-textfield">
      {label != null && <label className="a-textfield-label">{formatTextual(label)}</label>}
      <input
        type={inputType}
        className="a-textfield-input"
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      {error && <span className="a-textfield-error">{error}</span>}
    </div>
  );
}

// ─── Checkbox ───────────────────────────────────────────────────────────

export function ArtifactCheckbox({
  value: valueProp = false,
  label = '',
  __node_id,
  __clock,
}: {
  value?: boolean;
  label?: unknown;
  __node_id?: string;
  __clock?: VectorClock;
}) {
  const sendEvent = useArtifactSendEvent();
  const dispatch = useArtifactDispatch();
  const [checked, setChecked] = useState(valueProp);
  const clock = useValueClock(__clock);

  useEffect(() => {
    if (!clock.isFresh()) return;
    clock.accept();
    setChecked(valueProp);
    if (__node_id) dispatch({ type: 'SET_LIVE_VALUE', cuid: Number(__node_id), name: 'value', value: valueProp });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [__clock, valueProp, __node_id, dispatch]);

  return (
    <label className="a-checkbox">
      <input
        type="checkbox"
        checked={checked}
        onChange={() => {
          const next = !checked;
          setChecked(next);
          if (__node_id) {
            dispatch({ type: 'SET_LIVE_VALUE', cuid: Number(__node_id), name: 'value', value: next });
            const stamped = clock.bump();
            sendEvent(__node_id, 'change', { kind: 'CheckboxChanged', value: next, _clock: stamped });
            sendEvent(__node_id, next ? 'set' : 'clear', { kind: next ? 'CheckboxSet' : 'CheckboxCleared', value: next, _clock: stamped });
          }
        }}
      />
      <span className="a-checkbox-label">{formatTextual(label)}</span>
    </label>
  );
}

// ─── Picker ───────────────────────────────────────────────────────

export function ArtifactPicker({
  choices,
  value: valueProp,
  variant = 'dropdown',
  __node_id,
  __clock,
}: {
  choices?: Record<string, unknown>;
  value?: unknown;
  variant?: string;
  __node_id?: string;
  __clock?: VectorClock;
}) {
  const sendEvent = useArtifactSendEvent();
  const dispatch = useArtifactDispatch();
  const entries = Object.entries(choices ?? {});
  // Map JSON values to string indices for DOM elements
  const values = entries.map(([, v]) => v);

  const valueToIndex = (v: unknown): string => {
    const idx = values.findIndex((val) => JSON.stringify(val) === JSON.stringify(v));
    return idx >= 0 ? String(idx) : '';
  };

  const [selectedIndex, setSelectedIndex] = useState(() => valueToIndex(valueProp));
  const clock = useValueClock(__clock);

  useEffect(() => {
    if (!clock.isFresh()) return;
    clock.accept();
    setSelectedIndex(valueToIndex(valueProp));
    if (__node_id) dispatch({ type: 'SET_LIVE_VALUE', cuid: Number(__node_id), name: 'value', value: valueProp });
  }, [__clock, valueProp, __node_id, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (idx: string) => {
    setSelectedIndex(idx);
    const i = Number(idx);
    const val = Number.isFinite(i) ? values[i] : null;
    if (__node_id) {
      dispatch({ type: 'SET_LIVE_VALUE', cuid: Number(__node_id), name: 'value', value: val });
      sendEvent(__node_id, 'change', { kind: 'PickerChanged', value: val, _clock: clock.bump() });
    }
  };

  if (variant === 'chips') {
    return (
      <div className="a-choice-chips">
        {entries.map(([label], i) => (
          <button
            key={i}
            type="button"
            className={`a-choice-chip ${selectedIndex === String(i) ? 'a-choice-chip-active' : ''}`}
            onClick={() => handleChange(String(i))}
          >
            {formatTextual(label)}
          </button>
        ))}
      </div>
    );
  }

  if (variant === 'radio') {
    return (
      <div className="a-choice-radio">
        {entries.map(([label], i) => (
          <label key={i} className="a-choice-radio-option">
            <input
              type="radio"
              name={__node_id ?? 'choice'}
              value={String(i)}
              checked={selectedIndex === String(i)}
              onChange={() => handleChange(String(i))}
            />
            <span>{formatTextual(label)}</span>
          </label>
        ))}
      </div>
    );
  }

  return (
    <select
      className="a-choice-dropdown"
      value={selectedIndex}
      onChange={(e) => handleChange(e.target.value)}
    >
      <option value="">Select…</option>
      {entries.map(([label], i) => (
        <option key={i} value={String(i)}>
          {formatTextual(label)}
        </option>
      ))}
    </select>
  );
}

// ─── Slider ──────────────────────────────────────────────────────────

function stepDecimals(step: number): number {
  const s = step.toString();
  const dot = s.indexOf('.');
  return dot < 0 ? 0 : s.length - dot - 1;
}

function formatSliderValue(value: number, step: number | null | undefined, min: number, max: number): string {
  const effectiveStep = step ?? (max - min) / 1000;
  return value.toFixed(stepDecimals(effectiveStep));
}

export function ArtifactSlider({
  value: valueProp = 0,
  min = 0,
  max = 1,
  step,
  show_value = true,
  __node_id,
  __clock,
}: {
  value?: number;
  min?: number;
  max?: number;
  step?: number | null;
  show_value?: boolean;
  __node_id?: string;
  __clock?: VectorClock;
}) {
  const sendEvent = useArtifactSendEvent();
  const dispatch = useArtifactDispatch();
  const [localValue, setLocalValue] = useState(valueProp);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const clock = useValueClock(__clock);

  useEffect(() => {
    if (!clock.isFresh()) return;
    clock.accept();
    setLocalValue(valueProp);
    if (__node_id) dispatch({ type: 'SET_LIVE_VALUE', cuid: Number(__node_id), name: 'value', value: valueProp });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [__clock, valueProp, __node_id, dispatch]);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseFloat(e.target.value);
      setLocalValue(val);
      if (__node_id) dispatch({ type: 'SET_LIVE_VALUE', cuid: Number(__node_id), name: 'value', value: val });
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (__node_id) sendEvent(__node_id, 'change', { kind: 'SliderChanged', value: val, _clock: clock.bump() });
      }, 100);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [__node_id, sendEvent, dispatch],
  );

  return (
    <div className="a-slider">
      <input
        type="range"
        min={min}
        max={max}
        step={step ?? 'any'}
        value={localValue}
        onChange={handleChange}
        className="a-slider-input"
      />
      {show_value && <span className="a-slider-value">{formatSliderValue(localValue, step, min, max)}</span>}
    </div>
  );
}

// ─── ActionRow ─────────────────────────────────────────────────────────

export function ArtifactButtonRow({
  actions,
  __node_id,
}: {
  actions?: unknown;
  __node_id?: string;
}) {
  const sendEvent = useArtifactSendEvent();
  const entries = normalizeButtonRowActions(actions);

  return (
    <div className="a-action-row">
      {entries.map(({ key, label, busy = false, disabled = false }, index) => {
        const isDisabled = busy || disabled;
        return (
          <button
            key={String(key ?? index)}
            type="button"
            className={`a-action-btn ${busy ? 'is-busy' : ''}`}
            disabled={isDisabled}
            aria-busy={busy}
            onClick={() => {
              if (isDisabled) return;
              if (__node_id) sendEvent(__node_id, 'action', { kind: 'ButtonRowClicked', action: key });
            }}
          >
            {busy && <span className="a-button-spinner" aria-hidden="true" />}
            {formatTextual(label)}
          </button>
        );
      })}
    </div>
  );
}

function normalizeButtonRowActions(
  actions: unknown,
): Array<{ key: unknown; label: unknown; busy?: boolean; disabled?: boolean }> {
  if (Array.isArray(actions)) {
    return actions.flatMap((action) => {
      if (action == null || typeof action !== 'object') return [];
      const item = action as Record<string, unknown>;
      if (!('key' in item) || !('label' in item)) return [];
      return [
        {
          key: item.key,
          label: item.label,
          busy: item.busy_ref === true || item.busy === true,
          disabled: item.disabled_ref === true || item.disabled === true,
        },
      ];
    });
  }

  if (actions != null && typeof actions === 'object') {
    return Object.entries(actions as Record<string, unknown>).map(([key, value]) => {
      if (value != null && typeof value === 'object' && 'label' in (value as Record<string, unknown>)) {
        const item = value as Record<string, unknown>;
        return {
          key,
          label: item.label,
          busy: item.busy_ref === true || item.busy === true,
          disabled: item.disabled_ref === true || item.disabled === true,
        };
      }
      return { key, label: value };
    });
  }

  return [];
}
